import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { Plus, Hammer, Share2, Globe, Lock, Trash2, Play, Copy, Sparkles, Heart, Coins } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Courses() {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [mine, setMine] = useState([]);
  const [pub, setPub] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", base_city_id: "", description: "" });

  const load = async () => {
    try {
      const [c, m, p] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cities`),
        axios.get(`${BACKEND_URL}/api/courses/mine`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/courses/public`),
      ]);
      setCities(c.data.cities);
      setMine(m.data.courses || []);
      setPub(p.data.courses || []);
      if (!form.base_city_id && c.data.cities.length) {
        setForm((f) => ({ ...f, base_city_id: c.data.cities[0].id }));
      }
    } catch (e) {
      toast.error("Could not load courses");
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const create = async () => {
    if (!form.name.trim() || !form.base_city_id) {
      toast.error("Name + city are required.");
      return;
    }
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/courses/create`,
        form,
        { withCredentials: true },
      );
      toast.success("Course draft created");
      navigate(`/courses/edit/${r.data.course_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create course");
    }
  };

  const remove = async (course_id) => {
    if (!window.confirm("Delete this course? Cannot be undone.")) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/courses/${course_id}/delete`,
        {},
        { withCredentials: true },
      );
      toast.success("Course deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const copyShare = (code) => {
    const url = `${window.location.origin}/courses/play/${code}`;
    navigator.clipboard?.writeText(url);
    toast.success("Share link copied — paste to invite anyone");
  };

  const toggleLike = async (course_id) => {
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/courses/${course_id}/like`,
        {},
        { withCredentials: true },
      );
      // Optimistic patch — update both lists in place.
      setPub((list) =>
        list.map((c) =>
          c.course_id === course_id
            ? { ...c, likes: r.data.likes, is_liked_by_me: r.data.is_liked_by_me }
            : c,
        ),
      );
      setMine((list) =>
        list.map((c) =>
          c.course_id === course_id
            ? { ...c, likes: r.data.likes, is_liked_by_me: r.data.is_liked_by_me }
            : c,
        ),
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not like course");
    }
  };

  return (
    <AppLayout>
      <div className="px-6 lg:px-12 py-10 max-w-[1280px] mx-auto" data-testid="courses-page">
        <div className="label-kicker">Custom Courses</div>
        <h1 className="font-heading text-5xl lg:text-6xl mt-1">
          Build your own <span className="text-[#FFCC00]">heist</span>.
        </h1>
        <p className="text-white/60 mt-3 max-w-2xl text-sm">
          Describe an artifact, AI draws the icon (Nano Banana) and writes the trivia
          (Claude Sonnet 4.5). Publish and share the link — every racer gets pinged.
        </p>

        {/* Create form */}
        <div className="mt-10 surface-solid p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-kicker">New course</div>
              <div className="font-heading text-2xl mt-1">Start a draft</div>
            </div>
            <button
              className="btn-ghost text-xs"
              onClick={() => setCreating((v) => !v)}
              data-testid="courses-toggle-create"
            >
              {creating ? "Hide" : "Open form"}
            </button>
          </div>
          {creating && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-4">
                <div className="label-kicker mb-1">Course name</div>
                <input
                  data-testid="courses-create-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Colin's Detroit"
                  className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#FFCC00] outline-none"
                />
              </div>
              <div className="md:col-span-3">
                <div className="label-kicker mb-1">Base city</div>
                <select
                  data-testid="courses-create-city"
                  value={form.base_city_id}
                  onChange={(e) => setForm({ ...form, base_city_id: e.target.value })}
                  className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#FFCC00] outline-none"
                >
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-3">
                <div className="label-kicker mb-1">Description (opt)</div>
                <input
                  data-testid="courses-create-desc"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="A loot run through Motor City"
                  className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#FFCC00] outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <button
                  data-testid="courses-create-submit"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  onClick={create}
                >
                  <Plus className="w-4 h-4" /> Create
                </button>
              </div>
            </div>
          )}
        </div>

        {/* My courses */}
        <div className="mt-10">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <div className="label-kicker">Garage workshop</div>
              <div className="font-heading text-3xl mt-1">My Courses</div>
            </div>
            <div className="font-mono text-xs text-white/40">{mine.length}/12</div>
          </div>
          {mine.length === 0 ? (
            <div className="surface-solid p-8 text-center text-white/50 text-sm" data-testid="courses-mine-empty">
              No drafts yet. Make one above.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {mine.map((c) => (
                <div
                  key={c.course_id}
                  data-testid={`courses-mine-${c.course_id}`}
                  className="border border-white/10 p-4 hover:border-[#FFCC00] transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-heading text-xl truncate">{c.name}</span>
                        {c.is_published ? (
                          <span className="text-[9px] tracking-widest uppercase bg-[#34C759]/20 text-[#34C759] border border-[#34C759]/40 px-1.5 py-0.5">
                            <Globe className="w-2.5 h-2.5 inline mr-1" />Live
                          </span>
                        ) : (
                          <span className="text-[9px] tracking-widest uppercase bg-white/5 text-white/40 border border-white/10 px-1.5 py-0.5">
                            <Lock className="w-2.5 h-2.5 inline mr-1" />Draft
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] tracking-widest text-white/40 uppercase">
                        {c.base_city_name} · {c.items?.length || 0}/5 items
                      </div>
                      {c.is_published && (
                        <div className="mt-2 flex items-center gap-3 text-[11px] text-white/60">
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3 text-[#FF3B30]" fill="currentColor" />
                            <span className="font-mono">{c.likes ?? 0}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <Play className="w-3 h-3 text-[#FFCC00]" />
                            <span className="font-mono">{c.play_count ?? 0}</span>
                          </span>
                          <span
                            className="flex items-center gap-1 text-[#FFCC00]"
                            data-testid={`courses-mine-${c.course_id}-earnings`}
                            title="Coins earned from other racers running this course (25 per unique racer/day)."
                          >
                            <Coins className="w-3 h-3" />
                            <span className="font-mono">{c.creator_earnings ?? 0}</span>
                          </span>
                        </div>
                      )}
                      {c.description && (
                        <div className="text-xs text-white/60 mt-2 truncate">{c.description}</div>
                      )}
                    </div>
                    <button
                      data-testid={`courses-mine-${c.course_id}-delete`}
                      className="text-white/40 hover:text-[#FF3B30] p-1"
                      onClick={() => remove(c.course_id)}
                      title="Delete course"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      data-testid={`courses-mine-${c.course_id}-edit`}
                      className="flex-1 btn-ghost text-xs flex items-center justify-center gap-2"
                      onClick={() => navigate(`/courses/edit/${c.course_id}`)}
                    >
                      <Hammer className="w-3.5 h-3.5" /> Edit
                    </button>
                    {c.is_published && c.share_code && (
                      <>
                        <button
                          data-testid={`courses-mine-${c.course_id}-share`}
                          className="flex-1 btn-ghost text-xs flex items-center justify-center gap-2"
                          onClick={() => copyShare(c.share_code)}
                        >
                          <Share2 className="w-3.5 h-3.5" /> Share
                        </button>
                        <button
                          data-testid={`courses-mine-${c.course_id}-play`}
                          className="flex-1 btn-primary text-xs flex items-center justify-center gap-2"
                          onClick={() => navigate(`/courses/play/${c.share_code}`)}
                        >
                          <Play className="w-3.5 h-3.5" /> Run
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Public courses */}
        <div className="mt-10">
          <div className="flex items-baseline justify-between mb-5">
            <div>
              <div className="label-kicker">Worldwide</div>
              <div className="font-heading text-3xl mt-1">Public Courses</div>
            </div>
            <div className="font-mono text-xs text-white/40">{pub.length} live</div>
          </div>
          {pub.length === 0 ? (
            <div className="surface-solid p-8 text-center text-white/50 text-sm" data-testid="courses-public-empty">
              No public courses yet. Publish yours and be the first.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="courses-public-grid">
              {pub.map((c) => (
                <div
                  key={c.course_id}
                  data-testid={`courses-public-${c.course_id}`}
                  className="border border-white/10 p-4 hover:border-[#FFCC00] transition-colors group"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-3.5 h-3.5 text-[#FFCC00]" />
                    <span className="font-heading text-lg truncate">{c.name}</span>
                  </div>
                  <div className="text-[10px] tracking-widest text-white/40 uppercase">
                    by {c.owner_name} · {c.base_city_name}
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-[11px] text-white/50">
                    <span>{(c.items?.length || 0)} items</span>
                    <span>·</span>
                    <span>{c.play_count} plays</span>
                    <button
                      data-testid={`courses-public-${c.course_id}-like`}
                      onClick={() => toggleLike(c.course_id)}
                      className={`ml-auto flex items-center gap-1 transition-colors ${
                        c.is_liked_by_me
                          ? "text-[#FF3B30]"
                          : "text-white/40 hover:text-[#FF3B30]"
                      }`}
                      title={c.is_liked_by_me ? "Unlike" : "Like"}
                    >
                      <Heart
                        className="w-3.5 h-3.5"
                        fill={c.is_liked_by_me ? "currentColor" : "none"}
                      />
                      <span className="font-mono">{c.likes ?? 0}</span>
                    </button>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      data-testid={`courses-public-${c.course_id}-copy`}
                      className="btn-ghost text-xs flex items-center justify-center gap-2 flex-1"
                      onClick={() => copyShare(c.share_code)}
                    >
                      <Copy className="w-3.5 h-3.5" /> Link
                    </button>
                    <button
                      data-testid={`courses-public-${c.course_id}-play`}
                      className="btn-primary text-xs flex items-center justify-center gap-2 flex-1"
                      onClick={() => navigate(`/courses/play/${c.share_code}`)}
                    >
                      <Play className="w-3.5 h-3.5" /> Run
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
